// src/content/legal.js
// Text of the public legal pages. Plain data (no JSX) so it can be rendered
// twice from one source: by the React pages (Screens/LegalPage.jsx) and by
// scripts/prerender.mjs, which writes static HTML so reviewers and crawlers
// that don't run JavaScript still read the full text.
//
// Keep the seller and payment-provider details below in step with checkout.
//
// Inline tokens inside strings: {email} is the contact address, and
// {terms}, {refund}, {privacy}, {pricing} link to those pages.

export const SELLER = {
  legalName: "BuiltBySimi Studios",
  tradingName: "NameIt!",
  email: "hillaryd97@gmail.com",
  siteUrl: "https://name-it-ashy.vercel.app",
};

export const LEGAL_LINKS = {
  terms: { path: "/terms", label: "Terms of Service" },
  refund: { path: "/refund-policy", label: "Refund Policy" },
  privacy: { path: "/privacy-policy", label: "Privacy Policy" },
  pricing: { path: "/pricing", label: "Pricing" },
};

const UPDATED = "October 4, 2026";

export const LEGAL_PAGES = {
  terms: {
    path: "/terms",
    title: "Terms of Service",
    metaTitle: "Terms of Service | NameIt!",
    metaDescription:
      "The terms that apply when you play NameIt! or buy coins, seats or NameIt! Pro, including payments, subscriptions and acceptable use.",
    updated: UPDATED,
    intro:
      "These Terms of Service (the \"Terms\") apply to your use of the NameIt! website and apps (the \"Service\"). By using the Service you agree to them. If you don't agree, please don't use it.",
    sections: [
      {
        heading: "Who you are contracting with",
        paragraphs: [
          "NameIt! is provided by BuiltBySimi Studios (\"we\", \"us\"). When you use the Service you are contracting with BuiltBySimi Studios.",
          "You can contact us at {email}.",
          "For purchases paid outside Paystack's naira checkout, our payment provider Bachs processes checkout and payment collection. See \"Payments, taxes and subscriptions\" below.",
        ],
      },
      {
        heading: "Acceptance and changes",
        paragraphs: [
          "By continuing to use the Service you agree to these Terms. We may update them from time to time. If we make material changes we will update the date at the top of this page and, where appropriate, tell you through the Service. Continuing to use the Service after a change means you accept the updated Terms.",
        ],
      },
      {
        heading: "Using NameIt!",
        paragraphs: [
          "NameIt! is a free-to-play word game. You can play as a guest or create an account. Public rooms let you play with people you don't know, so please use good judgement, and parents or guardians should supervise younger players.",
          "You are responsible for what you do in the Service, including the names, answers, custom categories and messages you submit.",
        ],
      },
      {
        heading: "Acceptable use",
        paragraphs: ["You must not misuse the Service. In particular, you must not:"],
        bullets: [
          "use it for anything unlawful;",
          "commit fraud or send spam, or abuse or harass other players, including through player names, answers, categories or chat;",
          "infringe anyone else's intellectual property rights;",
          "interfere with the security or operation of the Service, for example by introducing malware, probing for weaknesses, scraping it, or using bots or automation to gain an advantage;",
          "try to get around limits, payments or purchases.",
        ],
      },
      {
        heading: "Your content",
        paragraphs: [
          "You keep ownership of the content you submit. You give us a non-exclusive, worldwide licence to store and show it inside the Service so the game works, for example displaying your answers to other players in your room. We may remove content that breaks these Terms.",
        ],
      },
      {
        heading: "Coins, seats and NameIt! Pro",
        paragraphs: ["Some features are paid. What you can buy:"],
        bullets: [
          "Coins: virtual items used inside NameIt!, for example to add player seats to a room. Coins have no cash value, can't be transferred or exchanged for money, and expire six months after they are purchased if unused.",
          "Seats: extra player slots for one room, added to that room for its session.",
          "NameIt! Pro: a monthly subscription that renews automatically until cancelled, or a one-time 1-month pass that does not renew. Pro currently includes larger rooms, unlimited rooms, a monthly coin bonus, and extra hints and dictionary checks. Benefits may change over time.",
        ],
        paragraphs2: [
          "Current prices are on our {pricing} page and are shown again at checkout before you pay. These are digital items that are delivered straight away, so purchases are final except as set out in our {refund}.",
        ],
      },
      {
        heading: "Payments, taxes and subscriptions",
        paragraphs: [
          "Payments in Nigerian naira are processed by Paystack. International card payments and Paystack fallback payments are processed by Bachs. For these purchases, we are the seller.",
          "Prices are shown at checkout. Applicable taxes, such as VAT or sales tax, are calculated at checkout based on your location and are included or added as shown there.",
          "The monthly Pro subscription is available through Paystack in naira and renews every month at the price shown when you subscribed until you cancel. You can cancel at any time using the cancellation link in your receipt email or by emailing {email}. Cancelling stops future renewals, and you keep Pro until the end of the period you already paid for. The 1-month pass does not renew.",
          "Refunds are covered by our {refund}.",
        ],
      },
      {
        heading: "Intellectual property",
        paragraphs: [
          "We own the Service and everything in it, including the software, game design, documentation and the NameIt! name and branding. These Terms give you a limited, personal, non-transferable right to use the Service; they don't transfer any ownership to you.",
        ],
      },
      {
        heading: "Availability of the Service",
        paragraphs: [
          "We work to keep NameIt! running, but we don't guarantee that it will be uninterrupted or error-free. It may be unavailable from time to time, for example for maintenance or because of problems outside our control. Game data is kept only briefly, as described in our {privacy}.",
        ],
      },
      {
        heading: "Suspension and termination",
        paragraphs: ["We may suspend or end your access to the Service, and remove unused coins, if:"],
        bullets: [
          "you materially breach these Terms;",
          "a payment fails or is reversed, or you don't pay what you owe;",
          "we reasonably believe there is a security or fraud risk; or",
          "you repeatedly or seriously break our rules, for example by harassing other players.",
        ],
        paragraphs2: [
          "You can stop using the Service at any time. To delete your account, contact {email}.",
        ],
      },
      {
        heading: "Disclaimers and liability",
        paragraphs: [
          "The Service is provided \"as is\" and \"as available\". To the fullest extent the law allows, we exclude all warranties not expressly stated here, and we are not liable for indirect or consequential losses. Our total liability to you for any claim relating to the Service is limited to the amount you paid us in the 12 months before the claim.",
          "Nothing in these Terms limits any liability or any consumer rights that can't be limited by law.",
        ],
      },
      {
        heading: "Governing law",
        paragraphs: [
          "These Terms are governed by the laws of the Federal Republic of Nigeria. This does not affect any mandatory consumer protection rights you have in the country where you live.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: ["Questions about these Terms? Email {email}."],
      },
    ],
  },

  refund: {
    path: "/refund-policy",
    title: "Refund Policy",
    metaTitle: "Refund Policy | NameIt!",
    metaDescription:
      "NameIt! coins, seats and Pro are digital purchases added to your account immediately. Refunds are available for double charges, wrong amounts, undelivered items and mistakes reported within 3 days.",
    updated: UPDATED,
    intro:
      "NameIt! coins, seats, and Pro are digital purchases that are added to your account immediately after payment. Because of this, purchases are generally final once they have been used. We know mistakes and payment issues can happen, though, so refunds are available in the situations below.",
    sections: [
      {
        heading: "Before you purchase",
        paragraphs: [
          "The item you are buying, its price, and the currency will be shown at checkout. Please check these details and make sure you are purchasing for the correct account or room before completing your payment.",
        ],
      },
      {
        heading: "When we will refund you",
        paragraphs: ["You can request a refund if:"],
        bullets: [
          "You were charged more than once for the same purchase.",
          "You were charged the wrong amount.",
          "You paid but did not receive what you purchased.",
          "You made a purchase by mistake, contact us within 3 days (72 hours), and have not used the purchase.",
        ],
        paragraphs2: ["Approved refunds will be returned to the original payment method."],
      },
      {
        heading: "After 3 days",
        paragraphs: [
          "Refund requests should normally be made within 3 days (72 hours) of payment.",
          "If more than 3 days have passed, you can still contact us. We will consider genuine circumstances on a case-by-case basis where appropriate or where required by applicable law.",
        ],
      },
      {
        heading: "Coins",
        paragraphs: [
          "Coins cannot be refunded once any of the purchased coins have been spent.",
          "If you purchase coins by mistake and contact us within 3 days, unused coins may be refunded to your original payment method.",
        ],
      },
      {
        heading: "Seats",
        paragraphs: [
          "Seats are added to your room immediately after purchase.",
          "If you purchase seats by mistake and contact us within 3 days, and the additional seats have not been used in a game, we can credit the value of the seats back to your NameIt! account as coins.",
          "A refund to your original payment method is available if you were charged more than once, charged the wrong amount, or did not receive the seats you purchased.",
        ],
      },
      {
        heading: "NameIt! Pro",
        subsections: [
          {
            heading: "Monthly subscription",
            paragraphs: [
              "You can cancel your subscription at any time. Your Pro access will continue until the end of the period you have already paid for, and you will not be charged for the next billing period.",
              "Payments for a billing period that has already started are generally non-refundable.",
              "If your subscription renews by mistake, contact us within 3 days of the renewal charge and we will review your refund request.",
            ],
          },
          {
            heading: "1-month pass",
            paragraphs: [
              "The 1-month Pro pass is a one-time purchase and does not automatically renew.",
              "The same 3-day refund rules described above apply.",
            ],
          },
        ],
      },
      {
        heading: "How to request a refund",
        paragraphs: ["Contact us at {email} and include:"],
        bullets: [
          "The email address associated with your NameIt! account.",
          "Your order or receipt number.",
          "A short explanation of what happened.",
        ],
        paragraphs2: [
          "For purchases processed by Bachs or Paystack, we may work with the payment provider to process eligible refunds.",
          "The time it takes for an approved refund to appear depends on your bank, card provider, or payment method.",
        ],
      },
      {
        heading: "Your legal rights",
        paragraphs: [
          "Nothing in this policy limits any consumer rights you may have under the laws that apply to you. Where applicable law gives you additional cancellation or refund rights, those rights will continue to apply.",
        ],
      },
      {
        heading: "Related policies",
        paragraphs: ["Please also see our {terms} and {privacy}."],
      },
    ],
  },

  privacy: {
    path: "/privacy-policy",
    title: "Privacy Policy",
    metaTitle: "Privacy Policy | NameIt!",
    metaDescription:
      "How NameIt! collects, uses and protects your information, including gameplay data, analytics and payments.",
    updated: UPDATED,
    effective: "August 27, 2025",
    intro:
      "This Privacy Policy explains how NameIt! (\"we\", \"us\", or \"our\"), provided by BuiltBySimi Studios, collects, uses, and handles information when you use the NameIt! website and mobile apps (the \"Services\").",
    sections: [
      {
        heading: "What we collect",
        paragraphs: ["Depending on how you use NameIt!, we may collect:"],
        bullets: [
          "Account information — such as your username and email address when you create an account.",
          "Gameplay information — such as games played, scores, rooms, game settings, and other information needed to provide NameIt! features.",
          "Device and technical information — such as device type, operating system, browser information, and technical logs.",
          "Analytics and diagnostics — information about how you use NameIt!, including interactions with features, performance information, crashes, and errors. We use PostHog to help us understand and improve the Service.",
          "Cookies and local storage — we may use cookies or local storage on the web and device storage on our mobile apps to maintain sessions, remember preferences, and support features.",
          "Purchase information — when you purchase coins, seats, or NameIt! Pro, payments are processed by our payment providers or the relevant app store. We may receive information such as your email address, transaction details, purchase status, and the item purchased. We do not receive or store your full card details.",
        ],
        paragraphs2: [
          "We do not intentionally collect sensitive personal information unless it is necessary to provide the Service or you choose to provide it to us.",
        ],
      },
      {
        heading: "How we use your information",
        paragraphs: ["We use information to:"],
        bullets: [
          "Provide and maintain NameIt! and its features.",
          "Create and manage accounts.",
          "Operate gameplay, multiplayer rooms, leaderboards, and other game features.",
          "Process and verify purchases.",
          "Remember your preferences and session information.",
          "Understand how people use NameIt! and improve its features, design, and performance.",
          "Diagnose crashes, errors, and technical problems.",
          "Detect and prevent abuse, fraud, or misuse of the Service.",
          "Respond to support requests and communicate with you when necessary.",
        ],
      },
      {
        heading: "Legal bases for EU and UK users",
        paragraphs: ["Where applicable, we process personal information based on one or more of the following:"],
        bullets: [
          "Performance of a contract — when processing is necessary to provide the Service or features you requested.",
          "Legitimate interests — where necessary to operate, secure, troubleshoot, and improve NameIt!, provided those interests are not overridden by your rights.",
          "Consent — where consent is required, such as for certain cookies or analytics technologies.",
          "Legal obligations — where we need to process or retain information to comply with applicable law.",
        ],
      },
      {
        heading: "Service providers",
        paragraphs: [
          "We use third-party services to help operate NameIt!. These may process limited information on our behalf or as otherwise described in their own policies.",
          "These services include:",
        ],
        bullets: [
          "Vercel — website hosting and delivery.",
          "Google Firebase — authentication, database, storage, and backend functionality.",
          "PostHog — product analytics and diagnostics.",
          "Bachs — checkout and payment processing for international card payments and fallback payments.",
          "Paystack — payment processing for purchases made in Nigerian naira.",
          "Apple App Store and Google Play — app distribution and, where applicable, payment processing.",
        ],
        paragraphs2: [
          "We may also disclose information where required by law, to protect our rights or users, or in connection with a business transfer.",
          "We do not sell your personal information.",
        ],
      },
      {
        heading: "Cookies and device storage",
        paragraphs: [
          "NameIt! may use cookies, local storage, and mobile device storage to keep you signed in, remember preferences, maintain game state, and support other features.",
          "We may also use these technologies in connection with analytics where permitted by law.",
          "You can control some cookies or stored information through your browser or device settings. Disabling them may affect how some parts of NameIt! work.",
        ],
      },
      {
        heading: "Data retention",
        paragraphs: [
          "We keep personal information only for as long as reasonably necessary for the purposes described in this policy, including providing the Service, maintaining accounts and purchases, resolving disputes, preventing abuse, and meeting legal obligations.",
          "Different types of information may be kept for different periods. Some temporary gameplay information may be automatically deleted after it is no longer needed, while account and transaction records may be retained for longer.",
          "When information is no longer required, we may delete or anonymize it.",
        ],
      },
      {
        heading: "Your choices and rights",
        paragraphs: [
          "Depending on where you live, you may have rights relating to your personal information, including the right to:",
        ],
        bullets: [
          "Access information we hold about you.",
          "Correct inaccurate information.",
          "Request deletion of your information.",
          "Request a copy or export of certain information.",
          "Object to or restrict certain processing.",
          "Withdraw consent where processing is based on consent.",
        ],
        paragraphs2: [
          "These rights may be subject to exceptions under applicable law.",
          "To make a privacy request, contact us using the details below.",
        ],
      },
      {
        heading: "Children",
        paragraphs: [
          "NameIt! is a family-friendly game, but some features allow players to join multiplayer rooms and interact with other players.",
          "We do not knowingly collect personal information from children in a way that requires parental consent without obtaining the required consent.",
          "Parents or guardians who believe a child has provided personal information inappropriately can contact us to request that we review or delete it.",
        ],
      },
      {
        heading: "Security",
        paragraphs: [
          "We use reasonable technical and organizational measures designed to protect personal information from unauthorized access, loss, misuse, or alteration.",
          "However, no online service or method of electronic storage can guarantee absolute security.",
        ],
      },
      {
        heading: "International data transfers",
        paragraphs: [
          "Some of our service providers operate in multiple countries. As a result, information may be processed or stored outside the country where you live.",
          "Where required by applicable law, appropriate safeguards are used for international transfers of personal information.",
        ],
      },
      {
        heading: "Contact us",
        paragraphs: [
          "If you have questions about this Privacy Policy or want to make a privacy request, contact:",
          "{email}",
        ],
      },
      {
        heading: "Changes to this policy",
        paragraphs: [
          "We may update this Privacy Policy as NameIt! changes or as legal requirements change.",
          "When we make changes, we will update the \"Last updated\" date at the top of this page. If a change materially affects how we handle personal information, we may also provide additional notice through the Service where appropriate.",
        ],
      },
      {
        heading: "Related policies",
        paragraphs: ["Please also see our {terms} and {refund}."],
      },
    ],
  },
};
